/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceHero.module.scss';

type Translate = (key: string) => string;

type AuditRow = {
  field: string;
  sub?: string;
  said: string;
  filed: string;
  source: string;
  flag?: string;
  mono?: boolean;
};

const getRows = (t: Translate): AuditRow[] => [
  {
    field: t('Legal entity'),
    said: 'Acme Logistics Ltd',
    filed: 'ACME LOGISTICS HOLDINGS LTD',
    source: 'Companies House · 08841207 · active',
  },
  {
    field: t('Registered office'),
    said: 'Unit 4, Brentford',
    filed: 'Unit 4 Brentford Trade Park, TW8 9DA',
    source: 'Confirmation statement · 11 Mar 2026',
  },
  {
    field: t('Principal activity'),
    sub: t('as classified on the register'),
    said: 'Office administration',
    filed: 'Treatment and disposal of hazardous waste',
    source: 'SIC 38220 · changed 04 Feb 2026',
    flag: t('Rating class'),
  },
  {
    field: t('Turnover'),
    sub: t('premium base'),
    said: '£4,200,000',
    filed: '£11,840,000',
    source: 'Filed accounts · FY to 31 Dec 2025',
    flag: t('2.8× declared'),
    mono: true,
  },
  {
    field: t('Employees'),
    said: '18',
    filed: '74',
    source: 'Filed accounts · average in period',
    flag: t('Exposure'),
    mono: true,
  },
  {
    field: t('Ultimate parent'),
    said: 'None declared',
    filed: 'MERIDIAN HOLDINGS S.à r.l.',
    source: 'Luxembourg RCS · 82% of issued capital',
    flag: t('Accumulation'),
  },
  {
    field: t('Directors'),
    said: '2 named',
    filed: '4 appointed, 1 resigned in period',
    source: 'Appointments · last change 22 Jun 2026',
  },
  {
    field: t('Outstanding charges'),
    said: 'Not stated',
    filed: '3 registered, none satisfied',
    source: 'Charges register · latest 09 May 2026',
  },
];

const ROW_STEP_MS = 130;
const REVEAL_MS = 520;

const STAT_ICONS = {
  registry: <path d="M4 10h16M6 10v9M10 10v9M14 10v9M18 10v9M5 19h14M12 3l8 5H4z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.6 2.3 4 5.3 4 8.5s-1.4 6.2-4 8.5c-2.6-2.3-4-5.3-4-8.5s1.4-6.2 4-8.5z" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3.5 21 8l-9 4.5L3 8z" />
      <path d="M3 13l9 4.5 9-4.5" />
      <path d="M3 17.5 12 22l9-4.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2.5" />
    </>
  ),
} as const;

const getStats = (t: Translate): { value: number; suffix: string; label: string; icon: keyof typeof STAT_ICONS }[] => [
  { value: 400, suffix: '+', label: t('Government registries, collected directly'), icon: 'registry' },
  { value: 200, suffix: '+', label: t('Countries and jurisdictions covered'), icon: 'globe' },
  { value: 600, suffix: 'M+', label: t('Company profiles'), icon: 'layers' },
  { value: 12, suffix: ' yrs', label: t('Of filing history behind every entity'), icon: 'clock' },
];

const LOGO_MARKS: Record<string, ReactNode> = {
  ring: (
    <>
      <circle cx="12.5" cy="12.5" r="9.5" />
      <path d="M12.5 3v19" />
    </>
  ),
  square: (
    <>
      <rect x="3" y="3" width="19" height="19" rx="4" />
      <path d="M3 12.5h19" />
    </>
  ),
  triangle: <path d="M12.5 2.5 22 20H3z" strokeLinejoin="round" />,
  house: <path d="M4 21V7l8.5-4.5L21 7v14" strokeLinejoin="round" />,
  wave: (
    <>
      <path d="M3 18.5c4-11 15-11 19 0" strokeLinecap="round" />
      <circle cx="12.5" cy="18.5" r="2.4" fill="currentColor" stroke="none" />
    </>
  ),
  mountain: (
    <>
      <path d="M4 20 12.5 5 21 20" strokeLinejoin="round" />
      <path d="M8 20h9" />
    </>
  ),
};

const LOGOS: { name: string; mark: keyof typeof LOGO_MARKS }[] = [
  { name: 'MERIDIAN UNDERWRITING', mark: 'ring' },
  { name: 'HALDEN RISK', mark: 'square' },
  { name: 'ASTOR SPECIALTY', mark: 'triangle' },
  { name: 'NORTHGATE MUTUAL', mark: 'house' },
  { name: 'CALLOWAY BROKING', mark: 'wave' },
  { name: 'VANTOR RE', mark: 'mountain' },
];

const MARQUEE_LOGOS = [...LOGOS, ...LOGOS, ...LOGOS, ...LOGOS, ...LOGOS];

export const InsuranceHero = () => {
  const { t } = useInt();
  const auditRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(auditRef, { threshold: 0.2 });
  const [playKey, setPlayKey] = useState(0);
  const [exceptions, setExceptions] = useState(0);
  const rows = getRows(t);
  const stats = getStats(t);
  const exceptionsTotal = rows.filter((row) => row.flag).length;
  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    setExceptions(0);
    const totalMs = ROW_STEP_MS * rows.length + REVEAL_MS;
    const id = setTimeout(() => setExceptions(exceptionsTotal), totalMs);

    return () => clearTimeout(id);
  }, [revealed, playKey]);

  useEffect(() => {
    if (!revealed) {
      return undefined;
    }

    const steps = 26;
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      const progress = Math.min(step / steps, 1);
      setCounts(stats.map((stat) => Math.round(stat.value * progress)));
      if (progress >= 1) {
        clearInterval(id);
      }
    }, 28);

    return () => clearInterval(id);
  }, [revealed]);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          size="page"
          title={t('Underwrite the company on the register, not the one on the submission.')}
          description={t(
            'Global Database sources company records directly from 400+ government registries in 200+ countries. Every field you rate, bind and reserve against carries the registry it came from, the filing reference, and the moment it was retrieved.',
          )}
        />
        <div className={styles.actions}>
          <Button type="blue" size="xxl" href="/book-a-demo">
            {t('Book a demo')}
          </Button>
          <Button type="dark-outline" size="xxl" href="/kyb-verification">
            {t('See how a submission is verified')}
          </Button>
        </div>
      </div>

      <div className={styles.marquee}>
        <div className={styles.marqueeTrack}>
          {MARQUEE_LOGOS.map((logo, i) => (
            <span className={styles.logoItem} key={`${logo.name}-${i}`}>
              <span className={styles.logoMark}>
                <svg viewBox="0 0 25 25" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                  {LOGO_MARKS[logo.mark]}
                </svg>
              </span>
              <span className={styles.logoName}>{logo.name}</span>
            </span>
          ))}
        </div>
      </div>
      <p className={styles.marqueeNote}>
        {t('Placeholder marks. Permissioned customer logos to replace before launch.')}
      </p>

      <div className={styles.auditWrap} ref={auditRef}>
        <div className={cn(styles.audit, revealed && styles.scanning)} key={playKey}>
          <div className={styles.scanline} />

          <div className={cn(styles.ar, styles.arHead)}>
            <span>{t('Field')}</span>
            <span>{t('As submitted')}</span>
            <span>{t('As filed · primary source')}</span>
            <span className={styles.markCol}>{t('Exception')}</span>
          </div>

          {rows.map((row, i) => (
            <div
              className={cn(styles.ar, row.flag && styles.arFlagged)}
              style={{ '--d': `${i * ROW_STEP_MS}ms` } as CSSProperties}
              key={row.field}
            >
              <span className={styles.fName}>
                {row.field}
                {row.sub && <span className={styles.fSub}>{row.sub}</span>}
              </span>
              <span className={cn(styles.fSaid, row.mono && styles.mono)}>{row.said}</span>
              <span>
                <span className={cn(styles.fFiled, row.mono && styles.mono)}>{row.filed}</span>
                <span className={styles.fProv}>{row.source}</span>
              </span>
              <span className={styles.markCol}>{row.flag && <span className={styles.mark}>{row.flag}</span>}</span>
            </div>
          ))}

          <div className={styles.auditFoot}>
            <span>Companies House · Registre de Commerce et des Sociétés</span>
            <span>
              {t('Retrieved')} <b>28 Jul 2026, 06:14 UTC</b>
            </span>
          </div>
        </div>

        <div className={styles.auditBar}>
          <span className={cn(styles.exceptions, exceptions > 0 && styles.exceptionsOn)}>
            {exceptions} {exceptions === 1 ? t('exception found') : t('exceptions found')}
          </span>
          <button type="button" className={styles.replay} onClick={() => setPlayKey((key) => key + 1)}>
            {t('Replay verification')}
          </button>
        </div>
      </div>

      <div className={cn(styles.stats, revealed && styles.statsOn)}>
        {stats.map((stat, i) => (
          <Link className={styles.stat} route="/data-dictionary" key={stat.label}>
            <span className={styles.statIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {STAT_ICONS[stat.icon]}
              </svg>
            </span>
            <span className={styles.statBody}>
              <span className={styles.statN}>
                {counts[i]}
                <em>{stat.suffix}</em>
              </span>
              <span className={styles.statL}>{stat.label}</span>
            </span>
          </Link>
        ))}
      </div>
    </Section>
  );
};
